<template>
  <Modal ref="modal" title="Faturamentos">
    <form class="export-container" @submit.prevent="handleSubmit">
      <Select :options="typeOptions" legend="Tipo de consulta" placeholder="Selecione um tipo" :modelValue="type"
        @update:modelValue="$event => (type = $event)" :disabled="loading" />

      <Select :options="sindicatoOptions" legend="Tipo de faturamento" placeholder="Selecione um tipo" :modelValue="sindicato"
        @update:modelValue="$event => (sindicato = $event)" :disabled="loading" />

      <Select :options="tipoAulaOptions" legend="Tipo de aula" placeholder="Selecione um tipo" :modelValue="tipoAula"
        @update:modelValue="$event => (tipoAula = $event)" :disabled="loading" data-testid="select-tipo-aula" />

      <Period legend="Insira o período" :firstModelValue="initialDate" :secondModelValue="finalDate"
        @update:firstModelValue="$event => (initialDate = $event)"
        @update:secondModelValue="$event => (finalDate = $event)" :disabled="loading" />

      <div class="format-container">
        <span>Em qual formato de exportação?</span>

        <RadioButtons :options="radioOptions" name="format" :modelValue="format"
          @update:modelValue="$event => format = $event" />
      </div>

      <Button type="submit" text="Fazer download" color="primary" :disabled="loading">
        <TrayArrowDown v-if="!loading" :size="18" />
        <component
          v-else
          :animation-duration="1000"
          :is="'SpringSpinner'"
          :color="'#fff'"
          :size="18"
          class="mx-2"
        >
        </component>
      </Button>
    </form>
  </Modal>
</template>

<script>
import Button from 'core/Button'
import Modal from 'core/Modal'
import Period from 'core/Period'
import RadioButtons from 'core/RadioButtons'
import Select from 'core/Select'
import TrayArrowDown from 'icons/TrayArrowDown'
import _export from '../../../../services/export'
import * as spinners from 'epic-spinners'

export default {
  name: 'FaturamentoModal',
  components: {
    Modal,
    Button,
    TrayArrowDown,
    Period,
    RadioButtons,
    Select,
    ...spinners,
  },
  data() {
    return {
      type: 'pagamento',
      sindicato: null,
      tipoAula: null,
      initialDate: '',
      finalDate: '',
      format: 'pdf',
      typeOptions: [
        { value: 'pagamento', text: 'Consultar pela data de pagamento' },
        { value: 'criacao', text: 'Consultar pela data de criação da fatura' },
      ],
      sindicatoOptions: [
        { value: null, text: 'Ambos' },
        { value: 'sindicato', text: 'Sindicato' },
        { value: 'itau', text: 'Itaú' },
      ],
      tipoAulaOptions: [
        { value: null, text: 'Todos' },
        { value: 'teorica', text: 'Aula Teórica' },
        { value: 'pratica_carro', text: 'Aula Prática Carro' },
        { value: 'pratica_moto', text: 'Aula Prática Moto' },
      ],
      radioOptions: [
        { value: "pdf", label: "PDF", info: "(Documento)" },
        { value: "csv", label: "CSV", info: "(Planilha)" },
        { value: "txt", label: "TXT", info: "(Texto sem formatação)" },
      ],
      loading: false,
    }
  },
  methods: {
    open() {
      this.$refs.modal.open()
    },
    close() {
      this.type = 'pagamento'
      this.sindicato = null
      this.tipoAula = null
      this.initialDate = ''
      this.finalDate = ''
      this.format = 'pdf'
      this.$refs.modal.close()
    },
    async handleSubmit() {
      if (!this.validateInputs()) {
        return
      }

      this.loading = true

      const params = {
        periodo: [this.initialDate, this.finalDate],
        tipo: this.format,
        data: this.type,
        sindicato: this.sindicato,
        tipo_aula: this.tipoAula,
      }
        await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/faturamento/exportar/faturas`, {
          params,
          responseType: params.tipo === 'csv' ? 'blob' : undefined
        })
        .then((res) => {
          switch (this.format) {
            case 'pdf': {
              _export.downloadPDF(res.body.arquivo, 'faturamentos')
              break
            }
            case 'csv': {
              _export.downloadCSV(res.data, 'faturamentos.csv')
              break
            }
            case 'txt': {
              _export.downloadTXT(res.data, 'faturamentos.txt')
              break
            }
          }
        })
        .catch((err) => {
          this.toast(`Erro ao tentar gerar relatório: ${err.body.mensagem}`)
        })
        .finally(() => {
          this.loading = false
        })
    },
    validateInputs() {
      if (!this.type) {
        this.toast('O campo de tipo de consulta é obrigatório.')
        return false
      }

      if (!this.initialDate || !this.finalDate) {
        this.toast('Os campos de período são obrigatórios.')
        return false
      }

      const initial = new Date(this.initialDate)
      const final = new Date(this.finalDate)

      if (final < initial) {
        this.toast('A data final deve ser menor que a data inicial!')
        return false
      }

      return true
    },
    toast(text) {
      this.$toasted.show(text, {
        icon: 'fa-times',
        position: 'top-right',
        duration: 5000,
        fullWidth: false
      })
    },
  }
}
</script>

<style lang="scss" scoped>
.export-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;

  .format-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    span {
      font-size: 1rem;
      font-weight: 600;
      line-height: 21.82px;
      color: #606264;
    }
  }

  button {
    justify-content: center;
  }
}
</style>

<style lang="scss">
/* Estilos globais para o modal de faturamento - garantir que dropdowns não sejam sobrepostos */
.export-container {
  .input-container {
    margin-bottom: 0.5rem;
    
    /* Aumenta z-index quando o select está ativo/focado */
    &:focus-within {
      z-index: 100 !important;
    }
  }
}

/* Garantir que o dropdown do vue-search-select apareça acima dos outros elementos */
.ui.fluid.dropdown {
  position: relative !important;
}

.ui.dropdown.active,
.ui.dropdown:focus-within {
  z-index: 9999 !important;
  position: relative !important;
}

.ui.dropdown .menu {
  z-index: 10000 !important;
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  margin-top: 0.25rem !important;
}

.ui.dropdown .menu.visible {
  z-index: 10001 !important;
}
</style>